import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useSession } from 'session/src/hooks/useSession'
import useSWRMutation from 'swr/mutation'
import { env } from 'utils/envManager'

import { useRef } from 'react'
import { chatBotGetAIResponse } from '../services/chatBotGetAIResponse'
import type { ConversationTrigger } from '../types/AIResponseTypes'

const baseUrl = '/v1/dispatcher'
const endpoint = new URL(baseUrl, env.NEXT_PUBLIC_IRIS_DOMAIN)
const abortReason = 'Chat dialog has been closed'

export const useChatBotResponse = () => {
	const {
		country: { countryISO, languageISO },
		isShop,
	} = useMasterData()

	const { externalId, token } = useSession()

	const abortControllerRef = useRef<AbortController | null>(null)
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

	const fetcher = (
		url: URL,
		{
			arg: { message, chatbotSessionId, conversationTrigger },
		}: {
			arg: {
				chatbotSessionId: string | null
				message?: string
				conversationTrigger?: ConversationTrigger
			}
		}
	) => {
		// Abort previous request if any
		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
		}

		abortControllerRef.current = new AbortController()
		const { signal } = abortControllerRef.current

		return chatBotGetAIResponse(url, {
			isOutlet: !isShop,
			sessionId: chatbotSessionId,
			externalId,
			message,
			languageISO,
			timeZone,
			countryISO,
			token,
			signal,
			conversationTrigger,
		})
	}

	const { trigger } = useSWRMutation(endpoint, fetcher)

	return {
		triggerAIResponse: trigger,
		abortAIRequest: () => abortControllerRef.current?.abort(abortReason),
	}
}
